import { Localized } from "@fluent/react"
import { Alert, AlertTitle, Box, Button, ButtonGroup, Container, Dialog, DialogActions, DialogContent, DialogTitle, Grid, ImageList, ImageListItem, Paper, TextField, Typography } from "@mui/material"
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react"
import { LoadSupplierDevices } from "../../app/AppDataLoader"
import { AppId } from "../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { LoadingContainer } from "../../app/LoadingContainer"
import { createSupplierDevice, selectAllSupplierDevices, selectSupplierDeviceById, updateSupplierDevice } from "./supplierDeviceSlice"
import AddIcon from "@mui/icons-material/Add"
import SaveIcon from '@mui/icons-material/Save'
import EditIcon from "@mui/icons-material/Edit"
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { RequiredError } from "../../app/components/RequiredError"
import { showError, showSuccess } from "../notifications/notificationsSlice"
import { LoadingButton } from "@mui/lab"
import { StructureName } from "../device/Structure"
import { StructureTreeSelect } from "../structure/StructureTreeSelect"
import { impossible } from "../../app/Utils"
import { useEditor } from "@tiptap/react"
import { RichTextContainer, tiptapExtensions } from "../../app/RichTextEditor"
import { BlobMeta, blobIdFromStoredRef, uploadBlobs } from "../documents/attachementsSlice"
import { BlobResourceType } from "../documents/blobResourceTypes"
import { BlobImage } from "../documents/BlobImage"
import { unwrapResult } from "@reduxjs/toolkit"
import { If } from "../../app/If"
import { loadManyBlobs, selectBlobsByResourceId } from "../documents/attachementsSlice"
import { createCustomStructure } from "../device/structureSlice"
import { SaveButton } from "../../app/components/SaveButton"
import { CancelButton } from "../../app/components/CancelButton"

type Mode
    = { tag: 'list' }
    | { tag: 'add' }
    | { tag: 'edit', deviceId: AppId }

export const SupplierDevicesList = () => {

    const [mode, setMode] = useState<Mode>({ tag: 'list' })

    const changeMode = useCallback((newMode: Mode) => setMode(newMode), [])

    let content = <></>
    switch (mode.tag) {
        case 'list':
            content = <List changeMode={changeMode} />
            break;
        case 'add':
            content = <Add changeMode={changeMode} />
            break;
        case 'edit':
            content = <Edit id={mode.deviceId} changeMode={changeMode} />
            break;
    }

    return <LoadSupplierDevices component={<LoadingContainer />}>
        {content}
    </LoadSupplierDevices>
}

interface CommonProps {
    changeMode: (mode: Mode) => void
}

const EmptyImage = () => {
    return <Box sx={{
        width: '300px',
        height: '200px',
        border: '2px dashed #ccc',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#888',
        // font-size: 20px;        /* Font size */
        textAlign: 'center',
        bgcolor: '#f9f9f9',
        // m: '20px auto',
    }}>
        No image
    </Box>
}

const List = (props: CommonProps) => {
    const { changeMode } = props
    const allDevices = useAppSelector(selectAllSupplierDevices)
    return <Container component={Paper} maxWidth='xl' sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        p: 2,
    }}>
        {allDevices.map(dev => <Box sx={{
                display: 'flex',
                gap: 4,
                [`& img`]: {
                    width: '300px',
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                    borderRadius: 1,
                },
            }}>
                <If condition={dev.url !== null} otherwise={<EmptyImage />}>
                    <BlobImage blobId={blobIdFromStoredRef(dev.url) ?? ''} alt='Device image' />
                </If>
                <Box sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}>
                    <Typography variant="h4">{dev.name}</Typography>
                    <Typography variant='body2' color='text.secondary'>
                        <StructureName structureId={dev.structureId} />
                    </Typography>
                    <Button startIcon={<EditIcon />} sx={{
                        mr: 'auto',
                    }} variant='outlined' onClick={() => changeMode({ tag: 'edit', deviceId: dev.id })}>
                        <Localized id='edit'>Edycja</Localized>
                    </Button>
                </Box>
            </Box>)}
        <Box>
            <Button onClick={() => changeMode(({ tag: 'add' }))} startIcon={<AddIcon />} variant='contained'>
                <Localized id='add'>Dodaj</Localized>
            </Button>
        </Box>
    </Container>
}

const Add = (props: CommonProps) => {
    const { changeMode } = props
    const dispatch = useAppDispatch();
    const [name, setName] = useState<string>("");    
    const [description, setDescription] = useState<string>("");
    const [structureId, setDeviceTypeId] = useState<string>("");
    const [saving, setSaving] = useState(false)
    const [validationErrors, setValidationErrors] = useState(false)

          
    const handleBackClick = useCallback(() => {
        changeMode({ tag: 'list' })
    }, [])
    
    const handleSaveClick = async () => {
        setSaving(true)
        setValidationErrors(true)

        if(name && structureId){
            setSaving(true)

            try {
                const { id: newId } = await dispatch(createSupplierDevice({
                    name,  
                    serialNo: "", 
                    productionYear: 0, 
                    description, 
                    structureId,
                    url: null,
                    guid: '',
                })).unwrap()

                dispatch(showSuccess("saved"));

                changeMode({ tag: 'edit', deviceId: newId })

            } catch (error) {
                dispatch(showError("error"));
            } finally {
                setSaving(false)
            }
        } 
        setSaving(false)
    }

    const handleNameChange: (e: ChangeEvent<HTMLInputElement>) => void = 
            useCallback(e => setName(e.target.value), []);

    const handleTypeChange = useCallback(structureId => setDeviceTypeId(structureId), [setDeviceTypeId])

    const nameError = validationErrors && name === ""

    return <Container component={Paper} maxWidth="lg" sx={{paddingTop: 2, paddingBottom: 2}}>
        <Grid container spacing={2} sx={{padding:2}}>
            <Grid item xs={12}>
                <TextField      
                    fullWidth    
                    required
                    error={nameError}
                    helperText={nameError && <RequiredError />}
                    multiline={true}       
                    inputProps={{ maxLength: 200 }}                                 
                    value={name}   
                    onChange={handleNameChange}                        
                    label={<Localized id="devices-name">Nazwa urzadzenia</Localized>}>
                </TextField>  
            </Grid>
            <Grid item xs={12}>
                <StructureTreeSelect defaultStructureId={undefined} onStructureSelected={handleTypeChange} />
            </Grid>
            <Grid item xs={12}> 
                <ButtonGroup color="primary" variant="contained">
                    <LoadingButton variant='contained' loading={saving} startIcon={<SaveIcon />} onClick={handleSaveClick}>
                        <Localized id="save">Zapisz</Localized>
                    </LoadingButton>
                    <LoadingButton loading={saving} onClick={handleBackClick}  variant="outlined">
                        <Localized id="back">Wróć</Localized>
                    </LoadingButton>
                </ButtonGroup>
            </Grid>
        </Grid>
    </Container>
}

const Edit = ({ id, changeMode }: { id: AppId } & CommonProps) => {
    const dispatch = useAppDispatch();
    const device = useAppSelector(state => selectSupplierDeviceById(state, id)) ?? impossible()
    const images = useAppSelector(state => selectBlobsByResourceId(state, device.guid))

    const [name, setName] = useState<string>(device.name);    
    const [description, setDescription] = useState<string>("");
    const [structureId, setDeviceTypeId] = useState<string>(device.structureId);
    const [saving, setSaving] = useState(false)
    const [validationErrors, setValidationErrors] = useState(false)
    const [focusedBlob, setFocusedBlob] = useState<BlobMeta | undefined>(undefined)
    const [customStructureDialogOpen, setCustomStructureDialogOpen] = useState(false)
    const [customStructureName, setCustomStructureName] = useState("")

    const imgCount = images?.blobs?.length ?? 0

    useEffect(() => {
        if (images === undefined) {
            dispatch(loadManyBlobs([device.guid]))
        }
    }, [])

          
    const handleBackClick = useCallback(() => {
        changeMode({ tag: 'list' })
    }, [])
    
    const handleSaveClick = async () => {
        setSaving(true)
        setValidationErrors(true)

        if(name && structureId){
            setSaving(true)

            try {
                await dispatch(updateSupplierDevice({
                    ...device,
                    name,
                    description: editor?.getHTML() ?? '',
                    structureId,
                })).unwrap()

                dispatch(showSuccess("saved"));

            } catch (error) {
                dispatch(showError("error"));
            } finally {
                setSaving(false)
            }
        } 
        setSaving(false)
    }

    const handleImageUpload = async (uri: string) => {
        await dispatch(updateSupplierDevice({
            ...device,
            url: uri,
        })).unwrap()
    }

    const handleNameChange: (e: ChangeEvent<HTMLInputElement>) => void = 
            useCallback(e => setName(e.target.value), []);

    const handleDescriptionChange: (e: ChangeEvent<HTMLInputElement>) => void = 
            useCallback(e => setDescription(e.target.value), [])

    const handleTypeChange = useCallback(structureId => setDeviceTypeId(structureId), [setDeviceTypeId])

    const nameError = validationErrors && name === ""

    const handleNextImgClick = () => {
        if (images && images.blobs) {
            const idx = images.blobs.findIndex(x => x.blobId === focusedBlob?.blobId)
            if (idx >= 0 && idx < images.blobs.length - 1) {
                setFocusedBlob(images.blobs[idx + 1])
            }
        }
    }

    const handlePrevImgClick = () => {
        if (images && images.blobs) {
            const idx = images.blobs.findIndex(x => x.blobId === focusedBlob?.blobId)
            if (idx > 0) {
                setFocusedBlob(images.blobs[idx - 1])
            }
        }
    }

    const setAsCoverImage = async () => {
        if (focusedBlob) {
            try {
                setSaving(true)
                await dispatch(updateSupplierDevice({
                    ...device,
                    // Persist the stable blobId; the cover <img> resolves it to a fresh
                    // signed URL at render time via <BlobImage>.
                    url: focusedBlob.blobId
                })).unwrap()

                dispatch(showSuccess("saved"));

            } catch (error) {
                dispatch(showError("error"));
            } finally {
                setSaving(false)
            }
        }
    }

    const handleCustomStructureSave = async () => {
        if (!customStructureName.trim()) {
            dispatch(showError("Structure name is required"));
            return;
        }

        setSaving(true)
        try {
            const result = await dispatch(createCustomStructure({
                structure: {
                    code: "",
                    name: customStructureName.trim(),
                    name_EN: customStructureName.trim(),
                    description: "",
                    description_EN: "",
                    parentStructureId: null,
                    subStructureIds: [],
                    grantAccessToAllCompanies: false,
                    grantAccessToAllModules: true,
                }
            })).unwrap();

            dispatch(showSuccess("saved"));
            setCustomStructureDialogOpen(false)
            setCustomStructureName("")
            
            if (result.id) {
                setDeviceTypeId(result.id);
            }
        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setSaving(false)
        }
    }

    const handleCustomStructureCancel = () => {
        setCustomStructureDialogOpen(false)
        setCustomStructureName("")
    }

    const editor = useEditor({
        extensions: tiptapExtensions,
        content: device.description,   // initial value
        // onUpdate: (e) => {                   // callback to get the updated value
        //     console.log(e.editor.getHTML())
        // },
    })

    return <Container component={Paper} maxWidth='xl' sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2,
    }}>
        <Box sx={{
            display: 'flex',
            gap: 2,
        }}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                [`& img`]: {
                    maxWidth: '300px',
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                    borderRadius: 1,
                },
            }}>
                {images && images.blobs && images.blobs.length > 0 && <ImageList gap={8} cols={1}>
                    {images.blobs.map(blob => <ImageListItem sx={{
                        [`& img`]: {
                            cursor: 'pointer',
                        },
                    }} key={blob.blobId}>
                        <BlobImage blobId={blob.blobId} loading='lazy' alt='Device image' onClick={() => setFocusedBlob(blob)} />
                    </ImageListItem>)}
                </ImageList>}
                <If condition={imgCount < 5}>
                    <FileUploader guid={device.guid} onUpload={handleImageUpload} />
                </If>
            </Box>

            <Box sx={{
                flexGrow: 1,
            }}>
                <Grid container spacing={2} sx={{padding:2}}>
                    <Grid item xs={12}>
                        <TextField      
                            fullWidth    
                            required
                            error={nameError}
                            helperText={nameError && <RequiredError />}
                            multiline={true}       
                            inputProps={{ maxLength: 200 }}                                 
                            value={name}   
                            onChange={handleNameChange}                        
                            label={<Localized id="devices-name">Nazwa urzadzenia</Localized>}>
                        </TextField>  
                    </Grid>
                    <Grid item xs={12}>
                        <StructureTreeSelect key={structureId} defaultStructureId={structureId} onStructureSelected={handleTypeChange} />
                    </Grid>
                    <Grid item xs={12}>
                        <Alert severity="info" sx={{ paddingLeft: 1, width: '100%', backgroundColor: 'background.paper' }} >
                            <AlertTitle sx={{ width: '100%' }}>
                                <>
                                    <Localized id="devices-custom-structure-info">_Can't find your structure? Create a new one.</Localized>
                                    <Button variant='outlined' color='primary' size='small' sx={{ ml: 1 }} startIcon={<AddIcon />} onClick={() => setCustomStructureDialogOpen(true)} >   
                                        <Localized id='add'>_Add</Localized>
                                    </Button>
                                </>
                            </AlertTitle>
                        </Alert>
                    </Grid>
                    <Grid item xs={12}>
                        <RichTextContainer label="Description" editor={editor} />
                    </Grid>
                    <Grid item xs={12}> 
                        <ButtonGroup color="primary" variant="contained">
                            <LoadingButton variant='contained' loading={saving} startIcon={<SaveIcon />} onClick={handleSaveClick}>
                                <Localized id="save">Zapisz</Localized>
                            </LoadingButton>
                            <LoadingButton loading={saving} onClick={handleBackClick}  variant="outlined">
                                <Localized id="back">Wróć</Localized>
                            </LoadingButton>
                        </ButtonGroup>
                    </Grid>
                </Grid>
            </Box>
        </Box>
        {focusedBlob && <Dialog fullScreen open={true}>
            <DialogContent sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                [`& img`]: {
                    maxWidth: '100%',
                    maxHeight: '100%',
                },
            }}>
                <BlobImage blobId={focusedBlob.blobId} loading='lazy' alt='Device image' />
            </DialogContent>
            <DialogActions>
                <LoadingButton loading={saving} color='primary' variant='contained' onClick={setAsCoverImage}>Set as cover image</LoadingButton>
                <Button onClick={handlePrevImgClick}>Previous</Button>
                <Button onClick={handleNextImgClick}>Next</Button>
                <Button onClick={() => setFocusedBlob(undefined)}>Close</Button>
            </DialogActions>
        </Dialog>}

        <Dialog open={customStructureDialogOpen} fullWidth maxWidth="sm">
            <DialogTitle
                sx={{
                    bgcolor: (theme) => theme.palette.grey[200],
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    py: 0.75,
                }}>
                <Localized id="devices-add-custom-structure">_Add Custom Structure</Localized>
            </DialogTitle>
            <DialogContent sx={{ pt: '10px !important' }}>
                <TextField
                    sx={{ mt: 1 }}
                    fullWidth
                    size="small"
                    label={<Localized id="structure-name">Structure Name</Localized>}
                    value={customStructureName}
                    onChange={(e) => setCustomStructureName(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} onClick={handleCustomStructureSave} />
                <CancelButton loading={saving} onClick={handleCustomStructureCancel} />
            </DialogActions>
        </Dialog>
    </Container>
}

export interface FileUploaderProps {
    guid: string
    onUpload: (fileUri: string) => Promise<void>
}
export const FileUploader = (props: FileUploaderProps) => {
    const { guid, onUpload } = props
    const dispatch = useAppDispatch()

    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const formRef = useRef<HTMLFormElement | null>(null)

    const handleSelectFilesClick = useCallback(() => {
        if (fileInputRef) {
            fileInputRef.current?.click()
        }
    }, [])
    const uploadFile = (fs: File[]): Promise<BlobMeta> => {
        return dispatch(uploadBlobs([guid, fs, { resourceType: BlobResourceType.SupplierDevice }]))
            .then(unwrapResult)
            .then(bms => {
                const bm = bms[bms.length - 1]
                return bm
            })
    }
    const handleInputFileChange = useCallback(async () => {
        if (fileInputRef.current) {
            const dt = fileInputRef.current.files
            if (dt && dt.length > 0) {
                for (const file of dt) {
                    const bm = await uploadFile([file])
                    await onUpload(bm.blobId)
                }
            }
        }
        if (formRef.current) {
            formRef.current.reset()
        }
    }, [])

    return <form ref={formRef}>
        <input
            ref={fileInputRef}
            style={{ display: 'none' }}
            type="file"
            multiple={false}
            value=''
            onChange={handleInputFileChange}
            accept="image/*"
        />
        <Button variant='outlined' startIcon={<CloudUploadIcon />} onClick={handleSelectFilesClick}>Upload image</Button>
    </form>
}
